<script setup lang="ts">
  import { BottomSheet, Button, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { useInfiniteScroll } from '@vueuse/core';
  import { computed, ref, watch } from 'vue';
  import { dataLayerTracker, DataLayerFilterInteraction } from '@/entities/analytics';
  import MobilePrimaryButton from '@/shared/ui/MobilePrimaryButton.vue';
  import SerpFilterGroup from './SerpFilterGroup.vue';
  import { SERP_DATA_LAYER_FILTER_PAGES } from '../config/dataLayer';
  import { SERP_FILTERS_LOAD_DISTANCE, SERP_FILTERS_PAGE_SIZE } from '../config/list';
  import { SERP_FILTERS_TEXTS } from '../config/texts';
  import { useSerp } from '../model/serpContainer';
  import { FilterGroupId, type SelectedFilter, type SerpFilterGroupId, type SerpFilterGroupView } from '../model/types';
  import { useSerpFilters } from '../model/useSerpFilters';

  interface SerpFiltersMobileProps {
    filtersGroups: SerpFilterGroupView[];
    selectedFilters: SelectedFilter[];
    changed: boolean;
    selected: boolean;
  }

  const props = defineProps<SerpFiltersMobileProps>();

  interface SerpFiltersMobileEmits {
    (e: 'close'): void;
    (e: 'apply'): void;
  }

  const emit = defineEmits<SerpFiltersMobileEmits>();

  const bottomSheetRef = ref<null | { close: () => Promise<void> }>(null);

  const { toggle, clearGroup, reset, filtersModelDraft } = useSerpFilters();

  const filterPage = SERP_DATA_LAYER_FILTER_PAGES[useSerp().productSearch.type];

  const contentRef = ref<HTMLElement | null>(null);
  const scrollContainer = computed(() => contentRef.value?.parentElement ?? null);

  const visibleCount = ref(SERP_FILTERS_PAGE_SIZE);
  const visibleGroups = computed(() => props.filtersGroups.slice(0, visibleCount.value));

  const { reset: recheckLoadMore } = useInfiniteScroll(
    scrollContainer,
    () => {
      visibleCount.value += SERP_FILTERS_PAGE_SIZE;
    },
    {
      distance: SERP_FILTERS_LOAD_DISTANCE,
      canLoadMore: () => visibleCount.value < props.filtersGroups.length,
    },
  );

  watch(() => props.filtersGroups.length, recheckLoadMore);

  let isApplied = false;

  const trackExclusiveInteraction = (): void => {
    if (!filterPage) return;

    dataLayerTracker.filterInteraction(DataLayerFilterInteraction.Click, filterPage);

    if (filtersModelDraft.value.isExclusive) {
      dataLayerTracker.filterInteraction(DataLayerFilterInteraction.CheckboxOn, filterPage);
    }
  };

  const onSelect = (groupId: SerpFilterGroupId, key: string): void => {
    toggle(groupId, key);

    if (groupId === FilterGroupId.IsExclusive) trackExclusiveInteraction();
  };

  const onReset = (groupId: SerpFilterGroupId): void => {
    clearGroup(groupId);
  };

  const onSubmit = async (): Promise<void> => {
    isApplied = props.changed;
    await bottomSheetRef.value?.close();
  };

  const onClose = (): void => {
    if (isApplied) emit('apply');
    emit('close');
  };
</script>

<template>
  <div
    class="serp-filters-mobile"
    data-testid="serp-filters-mobile"
  >
    <BottomSheet
      ref="bottomSheetRef"
      root-class="serp-filters-mobile__sheet"
      content-class="serp-filters-mobile__scroll"
      :animated="true"
      :height="'92%'"
      :icon-closable="true"
      @close="onClose"
    >
      <template #header>
        <Typo
          class="serp-filters-mobile__title"
          :variant="TypoVariant.Heading5"
          tag="span"
          data-testid="serp-filters-mobile-title"
        >
          {{ SERP_FILTERS_TEXTS.title }}
        </Typo>
      </template>

      <div
        ref="contentRef"
        class="serp-filters-mobile__content"
      >
        <component
          :is="group.component ?? SerpFilterGroup"
          v-for="group in visibleGroups"
          :key="group.id"
          class="serp-filters-mobile__section"
          :group="group"
          @select="onSelect"
          @reset="onReset"
        />
      </div>

      <template #footer>
        <div class="serp-filters-mobile__actions">
          <Button
            v-if="selected"
            class="serp-filters-mobile__reset"
            appearance="tertiary"
            data-testid="serp-filters-mobile-reset"
            @click="reset()"
          >
            {{ SERP_FILTERS_TEXTS.resetAll }}
          </Button>

          <MobilePrimaryButton
            class="serp-filters-mobile__submit"
            :testid="changed ? 'serp-filters-mobile-apply' : 'serp-filters-mobile-close'"
            @click="onSubmit"
          >
            {{ changed ? SERP_FILTERS_TEXTS.apply : SERP_FILTERS_TEXTS.close }}
          </MobilePrimaryButton>
        </div>
      </template>
    </BottomSheet>
  </div>
</template>

<style scoped lang="scss">
  .serp-filters-mobile {
    &__title {
      padding-top: var(--spacing-xxs);
      text-align: center;
      width: 100%;
    }

    &__content {
      display: flex;
      flex-direction: column;
    }

    &__section {
      padding: var(--spacing-l) 0;

      & + & {
        border-top: 1px dashed var(--color-neutral-30);
      }

      &:first-child {
        padding-top: 0;
      }

      &:last-child {
        padding-bottom: 0;
      }
    }

    &__actions {
      display: flex;
      gap: var(--spacing-xs);
      width: 100%;
    }

    &__reset {
      flex: 0 0 auto;
      height: 48px;
      max-width: 130px;
    }

    &__submit {
      flex: 1 1 0;
      min-width: 0;
    }
  }
</style>
